/**
 * AI Orchestrator
 * Manages AI selection, timing, and AI vs AI battles
 */

import { GameState, IMove, Position, IPiece } from "../../types";
import { ZodiacEngine } from "../../zodiacEngine";
import { IAIPlayer, AIPlayerType, BattleConfig, AIMoveResult } from "./IAIPlayer";
import { TimeManager } from "./TimeManager";
import { DionsAI } from "../DionsAI";
import { MinimaxAI } from "../MinimaxAI";
import { MonteCarloAI } from "../MonteCarloAI";
import { UltimateAI } from "../UltimateAI";
import { WispAI } from "../WispAI";
import { CreatureAI } from "../CreatureAI";
import { AstraAI } from "../AstraAI";
import { AstraBlindAI } from "../AstraBlindAI";

import { BlindAI } from "./BlindAI";

export interface PlayerSelection {
  type: AIPlayerType;
  instance?: IAIPlayer;
}

export interface BattleMove {
  player: "light" | "dark";
  playerType: AIPlayerType;
  fromPosition: Position;
  move: IMove;
  thinkingTime: number;
  confidence: number;
  evaluation?: number;
  reasoning?: string;
}

export interface BattleState {
  isActive: boolean;
  lightPlayer: AIPlayerType;
  darkPlayer: AIPlayerType;
  moves: BattleMove[];
  currentGameState: GameState;
}

export class AIOrchestrator {
  private gameEngine: ZodiacEngine;
  private aiInstances: Map<AIPlayerType, IAIPlayer> = new Map();
  private blindInstances = new Map<AIPlayerType, IAIPlayer>();
  private lightPlayer: PlayerSelection = { type: AIPlayerType.PLAYER };
  private darkPlayer: PlayerSelection = { type: AIPlayerType.DIONS_AI };
  private battleState: BattleState | null = null;

  // Timing configuration
  private minMoveDelay = 1000; // 1 second minimum
  private maxThinkingTime = 12000; // 12 seconds maximum

  constructor(gameEngine: ZodiacEngine) {
    this.gameEngine = gameEngine;
    this.initializeAIs();
  }

  /**
   * Initialize all AI instances
   */
  private initializeAIs(): void {
    this.aiInstances.set(AIPlayerType.DIONS_AI, new DionsAI(this.gameEngine));
    this.aiInstances.set(AIPlayerType.MINIMAX, new MinimaxAI(this.gameEngine));
    this.aiInstances.set(AIPlayerType.MCTS, new MonteCarloAI(this.gameEngine));
    this.aiInstances.set(AIPlayerType.ULTIMATE, new UltimateAI(this.gameEngine));
    this.aiInstances.set(AIPlayerType.WISP, new WispAI(this.gameEngine));
    this.aiInstances.set(AIPlayerType.CREATURE, new CreatureAI(this.gameEngine));
    this.aiInstances.set(AIPlayerType.ZSTR, new AstraAI(this.gameEngine));
    this.aiInstances.set(AIPlayerType.ZSTR_BLIND, new AstraBlindAI());
  }

  /**
   * Set light player
   */
  private getPlayerInstance(type: AIPlayerType, blind: boolean): IAIPlayer | undefined {
    if (!blind || type === AIPlayerType.PLAYER) return this.aiInstances.get(type);
    if (type === AIPlayerType.ZSTR || type === AIPlayerType.ZSTR_BLIND) return this.aiInstances.get(AIPlayerType.ZSTR_BLIND);
    if (![AIPlayerType.MCTS, AIPlayerType.MINIMAX, AIPlayerType.CREATURE].includes(type)) throw new Error(`Blind mode is unavailable for ${type}`);
    if (!this.blindInstances.has(type)) this.blindInstances.set(type, new BlindAI(type));
    return this.blindInstances.get(type);
  }

  setLightPlayer(type: AIPlayerType, blind = false): void {
    this.lightPlayer = {
      type,
      instance: this.getPlayerInstance(type, blind),
    };
  }

  /**
   * Set dark player
   */
  setDarkPlayer(type: AIPlayerType, blind = false): void {
    this.darkPlayer = {
      type,
      instance: this.getPlayerInstance(type, blind),
    };
  }

  /**
   * Get light player type
   */
  getLightPlayer(): AIPlayerType {
    return this.lightPlayer.type;
  }

  /**
   * Get dark player type
   */
  getDarkPlayer(): AIPlayerType {
    return this.darkPlayer.type;
  }

  /**
   * Get AI instance by type
   */
  getAI(type: AIPlayerType): IAIPlayer | undefined {
    return this.aiInstances.get(type);
  }

  /**
   * Check if current player is AI
   * CRITICAL: Also handles bonus turns - if a bonus turn is active, check which side owns the bonus turn piece
   */
  isCurrentPlayerAI(gameState: GameState): boolean {
    // If there's an active bonus turn, check which side owns the bonus turn piece
    if (gameState.skipTurnChange && gameState.activeBonusTurnPieceId) {
      // Find the bonus turn piece to determine which side it belongs to
      const bonusTurnPiece = this.findPieceById(gameState, gameState.activeBonusTurnPieceId);
      if (bonusTurnPiece) {
        const bonusTurnPlayer = bonusTurnPiece.isLight ? this.lightPlayer : this.darkPlayer;
        return bonusTurnPlayer.type !== AIPlayerType.PLAYER;
      }
    }

    // Normal turn - check current player
    const currentPlayer = gameState.isLightTurn ? this.lightPlayer : this.darkPlayer;
    return currentPlayer.type !== AIPlayerType.PLAYER;
  }

  /**
   * Get current AI instance
   * CRITICAL: Also handles bonus turns - if a bonus turn is active, get AI for the bonus turn piece's side
   */
  getCurrentAI(gameState: GameState): IAIPlayer | undefined {
    // If there's an active bonus turn, get AI for the bonus turn piece's side
    if (gameState.skipTurnChange && gameState.activeBonusTurnPieceId) {
      // Find the bonus turn piece to determine which side it belongs to
      const bonusTurnPiece = this.findPieceById(gameState, gameState.activeBonusTurnPieceId);
      if (bonusTurnPiece) {
        const bonusTurnPlayer = bonusTurnPiece.isLight ? this.lightPlayer : this.darkPlayer;
        return bonusTurnPlayer.instance;
      }
    }

    // Normal turn - get current player's AI
    const currentPlayer = gameState.isLightTurn ? this.lightPlayer : this.darkPlayer;
    return currentPlayer.instance;
  }

  /**
   * Helper to find a piece by ID in the game state
   */
  private findPieceById(gameState: GameState, pieceId: string): IPiece | null {
    for (const row of gameState.board) {
      for (const square of row) {
        for (const piece of square.Pieces) {
          if (piece.id === pieceId) {
            return piece;
          }
        }
      }
    }
    return null;
  }

  /**
   * Request AI move for current player
   * Handles timing constraints automatically
   * @param gameState Current game state
   * @param customTimeLimit Optional custom time limit (defaults to this.maxThinkingTime)
   */
  async requestAIMove(gameState: GameState, customTimeLimit?: number): Promise<AIMoveResult | null> {
    const ai = this.getCurrentAI(gameState);
    if (!ai) return null;

    const timeLimit = customTimeLimit ?? this.maxThinkingTime;


    try {
      // Execute with timing constraints
      const result = await TimeManager.executeWithTiming(
        () => ai.selectMove(gameState, timeLimit),
        {
          minDelay: this.minMoveDelay,
          maxThinkingTime: timeLimit,
        }
      );


      // 20% chance AI makes a king guess taunt
      if (Math.random() < 0.2) {
        const aiGuess = this.generateAIKingGuess(gameState);
        if (aiGuess) {
          result.evaluationBreakdown = {
            ...result.evaluationBreakdown,
            kingGuess: aiGuess,
          };
        }
      }

      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Generate a random king guess for the AI taunt feature
   * AI picks a random opponent piece to guess as the king
   */
  private generateAIKingGuess(gameState: GameState): { pieceId: string; position: Position } | null {
    const opponentPieces: { piece: IPiece; position: Position }[] = [];

    // Find all opponent pieces
    // If it's light's turn, AI is playing as light, so opponent is dark (isLight === false)
    // If it's dark's turn, AI is playing as dark, so opponent is light (isLight === true)
    const opponentIsLight = !gameState.isLightTurn;

    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        for (const piece of square.Pieces) {
          if (piece.isLight === opponentIsLight) {
            opponentPieces.push({ piece, position: { row, col } });
          }
        }
      }
    }

    if (opponentPieces.length === 0) return null;

    // Pick a random opponent piece
    const randomIndex = Math.floor(Math.random() * opponentPieces.length);
    const target = opponentPieces[randomIndex];

    return {
      pieceId: target.piece.id,
      position: target.position,
    };
  }

  /**
   * Start AI vs AI battle
   */
  startBattle(config: BattleConfig, initialGameState: GameState): void {
    this.setLightPlayer(config.lightPlayer);
    this.setDarkPlayer(config.darkPlayer);
    this.minMoveDelay = config.minMoveDelay;
    this.maxThinkingTime = config.maxThinkingTime;

    this.battleState = {
      isActive: true,
      lightPlayer: config.lightPlayer,
      darkPlayer: config.darkPlayer,
      moves: [],
      currentGameState: initialGameState,
    };

  }

  /**
   * Stop current battle
   */
  stopBattle(): void {
    if (this.battleState) {
      this.battleState.isActive = false;
    }
  }

  /**
   * Get current battle state
   */
  getBattleState(): BattleState | null {
    return this.battleState;
  }

  /**
   * Check if battle is active
   */
  isBattleActive(): boolean {
    return this.battleState?.isActive || false;
  }

  /**
   * Record battle move
   */
  recordBattleMove(
    gameState: GameState,
    moveResult: AIMoveResult,
    playerType: AIPlayerType
  ): void {
    if (!this.battleState) return;

    const battleMove: BattleMove = {
      player: gameState.isLightTurn ? "dark" : "light", // Move was just made, so player switched
      playerType,
      fromPosition: moveResult.fromPosition,
      move: moveResult.move,
      thinkingTime: moveResult.thinkingTime,
      confidence: moveResult.confidence,
      evaluation: moveResult.evaluation,
      reasoning: moveResult.reasoning,
    };

    this.battleState.moves.push(battleMove);
    this.battleState.currentGameState = gameState;
  }

  /**
   * Get display name for player type
   */
  getPlayerName(type: AIPlayerType): string {
    const ai = this.aiInstances.get(type);
    return ai?.displayName || type;
  }

  /**
   * Get all available AI types
   */
  getAvailableAIs(): Array<{ type: AIPlayerType; name: string; description: string }> {
    const ais: Array<{ type: AIPlayerType; name: string; description: string }> = [
      {
        type: AIPlayerType.PLAYER,
        name: "Player",
        description: "Human player",
      },
    ];

    for (const [type, ai] of this.aiInstances) {
      ais.push({
        type,
        name: ai.displayName,
        description: ai.description,
      });
    }

    return ais;
  }

  /**
   * Reset all AIs
   */
  resetAIs(): void {
    this.blindInstances.forEach(ai => ai.reset());
    for (const ai of this.aiInstances.values()) {
      ai.reset();
    }
  }

  /**
   * Configure timing constraints
   */
  setTimingConstraints(minDelay: number, maxThinkingTime: number): void {
    this.minMoveDelay = minDelay;
    this.maxThinkingTime = maxThinkingTime;
  }

  /**
   * Get timing constraints
   */
  getTimingConstraints(): { minDelay: number; maxThinkingTime: number } {
    return {
      minDelay: this.minMoveDelay,
      maxThinkingTime: this.maxThinkingTime,
    };
  }

  /**
   * Get statistics for all AIs
   */
  getAllStats(): Map<AIPlayerType, any> {
    const stats = new Map();

    for (const [type, ai] of this.aiInstances) {
      stats.set(type, {
        name: ai.displayName,
        stats: ai.getStats(),
      });
    }

    return stats;
  }
}
